import React, { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { EntityChatParticipants } from "./entity-chat-participants"
import {
  flattenChronologically,
  resolveUnreadMessageIds,
  resolveUnreadMessageIdsForAudience,
  unreadCountAfterToggle
} from "../lib/read-state"
import { useTranslation } from 'react-i18next'
import { Button } from "./ui/button"
import { Badge } from "./ui/badge"
import { Switch } from "./ui/switch"

import { TiptapHtmlEditor, type TiptapHtmlEditorRef } from "./ui/tiptap-html-editor"
import { HtmlContentRenderer } from "./ui/html-content-renderer"
import { Checkbox } from "./ui/checkbox"
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip"
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs"
import { X, Home, Users, Lock, ChevronDown, Eye, Reply, Filter, ArrowDown, RefreshCw, Bell, BellOff } from 'lucide-react'
import { initialsFrom } from "../lib/avatar"
import { MessageAvatar } from "./ui/message-avatar"
import {
  allowMarkRead,
  isMarkReadSuppressedKey,
  suppressMarkRead
} from "../lib/mark-read-suppression"
import { cn, sxToStyleTag, mergeSx, camelToKebab } from "../lib/utils"
import { useMessageComponents } from "./message-components-provider"
import { initializeI18n } from "../lib/i18n"
import { useMessageApi } from "../hooks/use-message-api"
import { notifyConversationsChanged } from "../services/conversation-summary-batcher"
import type { Message, RelatedEntity, EntityChatPanelProps, User } from "../types"



// Display-label overrides for entity types whose generic camelCase split reads poorly
// (acronym casing, redundant words). Anything not listed falls back to the splitter.
const ENTITY_TYPE_LABELS: Record<string, string> = {
  AtsPlacementRequest: 'ATS Placement',
  VmsRequest: 'VMS Request',
}

// Component for rendering individual messages with thread support
interface MessageThreadProps {
  message: Message
  selectedCount: number
  user: User
  messageTab: string
  activeReplyIds: Set<string>
  replyMessages: Map<string, string>
  setReplyMessage: (messageId: string, message: string) => void
  replyImportance: Map<string, boolean>
  setReplyImportance: (messageId: string, isImportant: boolean) => void

  replyHtmlEditorRef: React.RefObject<TiptapHtmlEditorRef | null>
  onStartReply: (messageId: string) => void
  onCancelReply: (messageId: string) => void
  sendReply: (parentId: string) => void
  getRelativeTime: (date: Date | string) => string
  getExactTimestamp: (date: Date | string) => string
  formatEntityBadge: (entityTypeId: string, entityId: string) => string
  getInitials: (name: string) => string
  hasCurrentEntitySelected: boolean
  hasAnyEntitySelected: boolean
  currentEntityKey: string
  depth?: number
  /** Read state is only drawn for a single conversation — see resolveUnreadMessageIds. */
  isUnread?: boolean
  isFirstUnread?: boolean
  showReadToggle?: boolean
  isTogglingRead?: boolean
  onToggleRead?: (message: Message, read: boolean) => void
  t: (key: string, options?: any) => string
}

const MessageThread = React.forwardRef<HTMLDivElement, MessageThreadProps>(({
  message,
  selectedCount,
  user,
  messageTab,
  activeReplyIds,
  replyMessages,
  setReplyMessage,
  replyImportance,
  setReplyImportance,

  replyHtmlEditorRef,
  onStartReply,
  onCancelReply,
  sendReply,
  getRelativeTime,
  getExactTimestamp,
  formatEntityBadge,
  getInitials,
  hasCurrentEntitySelected,
  hasAnyEntitySelected,
  currentEntityKey,
  depth = 0,
  isUnread = false,
  isFirstUnread = false,
  showReadToggle = false,
  isTogglingRead = false,
  onToggleRead,
  t
}, ref) => {
  const maxDepth = 1 // Only allow 1 level of nesting
  const isReplyingToThis = activeReplyIds.has(message.id)
  const replyInputAreaRef = useRef<HTMLDivElement>(null)

  // Check if this message belongs to the current entity
  const messageEntityKey = `${message.entityTypeId}:${message.entityId}`
  const isCurrentEntityMessage = messageEntityKey === currentEntityKey



  return (
    <React.Fragment key={message.id}>
      {/* The NEW divider is a separator ABOVE this thread, not inside it — the red rule spans the gap
          between the older and newer cards instead of sitting within the newer message's card. It stays
          where it was when the panel opened (a "where was I" marker), not following the scroll. */}
      {isFirstUnread && (
        <div className="entity-chat-panel-new-divider" role="separator" aria-label={t('messages.newDivider')}>
          <span className="entity-chat-panel-new-divider-label">{t('messages.newDivider')}</span>
        </div>
      )}
      <div
        data-message-id={message.id}
        ref={ref}
        className={cn(
          "entity-chat-panel-message-thread",
          isUnread && "entity-chat-panel-message-thread-unread",
          depth > 0 && "entity-chat-panel-reply-message ml-12 border-l-2 border-muted pl-4 mt-2"
        )}
      >
      {/* Main Message */}
      <div
        className="entity-chat-panel-message"
        data-important={message.isImportant}
      >
        {/* Avatar — the same component the participant list renders, so one person is one circle
            everywhere. It owns the colour, the initials and the white letters; the legacy class stays
            only for the layout rules that already target it. */}
        <MessageAvatar
          name={message.authorName}
          size={26}
          className="entity-chat-panel-message-avatar"
        />

        {/* Message content */}
        <div className="entity-chat-panel-message-content">
          <div className="entity-chat-panel-message-header">
            <div>
              <span className="entity-chat-panel-message-author">{message.authorName}</span>
              {/* Audience pill — Client vs Internal, text only (the eye icon is gone). Only on the All
                  tab, where the two lanes are interleaved and the label is the only thing telling them
                  apart; on the Internal/Client tabs every row is already that lane, so the pill is just
                  noise. A client user never sees it — they have no tabs and only ever see client messages.
                  Sits right after the name so a tight row truncates the timestamp, not the label. */}
              {user.type === "internal" && messageTab === "all" && (
                <Badge
                  variant="secondary"
                  className={cn(
                    "entity-chat-panel-message-visibility-badge",
                    message.isVisibleToClient
                      ? "entity-chat-panel-message-visibility-client"
                      : "entity-chat-panel-message-visibility-internal"
                  )}
                >
                  {message.isVisibleToClient ? t('visibility.client') : t('visibility.internal')}
                </Badge>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="entity-chat-panel-message-time">
                    {getRelativeTime(message.createdDateTime)}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{getExactTimestamp(message.createdDateTime)}</p>
                </TooltipContent>
              </Tooltip>
              {/* Show entity badge only when multiple entities are selected */}
              {selectedCount > 1 && (
                <Badge
                  variant="outline"
                  className={cn(
                    "entity-chat-panel-message-entity-badge",
                    "entity-chat-panel-message-entity-pill",
                    `entity-chat-panel-message-entity-${message.entityTypeId.toLowerCase()}-pill`
                  )}
                >
                  {formatEntityBadge(message.entityTypeId, message.entityId)}
                </Badge>
              )}
            </div>
            {/* Show migrated badge and/or importance icon - migrated first, importance always on far right */}
            <div className="entity-chat-panel-message-indicators">
              {/* Reads as a per-message switch but behaves as a slider, because the underlying state
                  is a watermark: marking read covers everything older, marking unread stops just
                  before this message. Only offered on root messages — a reply and its parent share
                  one boundary, so a toggle on each would look like two independent switches that
                  move together. */}
              {showReadToggle && depth === 0 && onToggleRead && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className={cn(
                        "entity-chat-panel-message-read-toggle",
                        isUnread && "entity-chat-panel-message-read-toggle-unread"
                      )}
                      disabled={isTogglingRead}
                      aria-pressed={!isUnread}
                      onClick={() => onToggleRead(message, isUnread)}
                    >
                      <span className="entity-chat-panel-message-read-label">
                        {isUnread ? t('messages.markRead') : t('messages.markUnread')}
                      </span>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{isUnread ? t('tooltips.markRead') : t('tooltips.markUnread')}</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {message.originalNoteId && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="entity-chat-panel-message-migrated-wrapper">
                      <Badge variant="secondary" className="entity-chat-panel-message-migrated-badge">
                        M
                      </Badge>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('tooltips.migrated')}</p>
                  </TooltipContent>
                </Tooltip>
              )}
              {message.isImportant && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="entity-chat-panel-message-importance-wrapper">
                      {/* Labelled, not just punctuated. A lone "!" is a mark people learn to read past;
                          the word is what makes it register in a column of similar-looking cards. */}
                      <span className="entity-chat-panel-message-importance-icon">!</span>
                      <span className="entity-chat-panel-message-importance-label">
                        {t('tooltips.importantLabel')}
                      </span>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('tooltips.important')}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
          <div className="entity-chat-panel-message-text">
            <HtmlContentRenderer
              content={message.content}
              contentType={message.contentType || "html"}
            />
          </div>


        </div>
      </div>

      {/* Replies */}
      {depth < maxDepth && message.replies && message.replies.length > 0 && (
        <div className="entity-chat-panel-replies">
          {message.replies.map((reply) => (
            <MessageThread
              key={reply.id}
              message={reply}
              selectedCount={selectedCount}
              user={user}
              messageTab={messageTab}
              activeReplyIds={activeReplyIds}
              replyMessages={replyMessages}
              setReplyMessage={setReplyMessage}
              replyImportance={replyImportance}
              setReplyImportance={setReplyImportance}

              replyHtmlEditorRef={replyHtmlEditorRef}
              onStartReply={onStartReply}
              onCancelReply={onCancelReply}
              sendReply={sendReply}
              getRelativeTime={getRelativeTime}
              getExactTimestamp={getExactTimestamp}
              formatEntityBadge={formatEntityBadge}
              getInitials={getInitials}
              hasCurrentEntitySelected={hasCurrentEntitySelected}
              hasAnyEntitySelected={hasAnyEntitySelected}
              currentEntityKey={currentEntityKey}
              depth={depth + 1}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Reply Button and Input - only show on root messages (depth 0) and position after all replies */}
      {depth === 0 && (
        <div className="entity-chat-panel-thread-reply-section">
          {/* Reply Button - only show when NOT replying to this message and message belongs to current entity */}
          {!activeReplyIds.has(message.id) && isCurrentEntityMessage && (
            <div className="entity-chat-panel-message-actions">
              <Button
                variant="ghost"
                size="sm"
                className="entity-chat-panel-reply-button"
                onClick={() => onStartReply(message.id)}
                disabled={!hasAnyEntitySelected || !hasCurrentEntitySelected || (user.type === "internal" && messageTab === "all")}
              >
                <Reply className="entity-chat-panel-reply-icon" />
                {t('messages.reply')}
              </Button>
            </div>
          )}

          {/* Inline Reply Input - only show for current entity messages */}
          {activeReplyIds.has(message.id) && isCurrentEntityMessage && (
            <div ref={replyInputAreaRef} className="entity-chat-panel-reply-input-area" data-replying-to={message.id}>
              <TiptapHtmlEditor
                ref={replyHtmlEditorRef as React.Ref<TiptapHtmlEditorRef>}
                value={replyMessages.get(message.id) || ""}
                onChange={(value) => setReplyMessage(message.id, value)}
                onSend={() => sendReply(message.id)}
                placeholder={
                  user.type === "client"
                    ? t('messages.replyPlaceholder')
                    : messageTab === "internal"
                      ? t('messages.replyPlaceholderInternal')
                      : t('messages.replyPlaceholderClient')
                }
                disabled={!hasAnyEntitySelected || !hasCurrentEntitySelected || (user.type === "internal" && messageTab === "all")}
                className="entity-chat-panel-reply-html-editor"
                showSendButton={true}
                isImportant={replyImportance.get(message.id) || false}
                onImportanceChange={(isImportant) => setReplyImportance(message.id, isImportant)}
              />
              <div className="entity-chat-panel-reply-actions">
                <button type="button"
                  onClick={() => onCancelReply(message.id)}
                  className="entity-chat-panel-reply-cancel-link"
                >
                  {t('messages.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </React.Fragment>
  )
})

MessageThread.displayName = 'MessageThread'

export default function EntityChatPanel({
  entityTypeId,
  entityId,
  onClose,
  className,
  sx,
  language = "en",
  customTranslations,
  onMessageCountChanged,
  title,
  showCloseButton = false,
  internalOnly = false,
  showRelatedEntities = true,
  autoIncludeRelatedTypes = [],
  markReadOnOpen = true,
  onUnreadChange,
  headerSubtitle,
  headerRecordLabel,
  headerRecordHref,
  headerInitials,
  headerAvatarColor,
  entityAlreadyRead = false,
  initialUnreadCount,
  initialIsSubscribed,
}: EntityChatPanelProps) {
  // Initialize i18n synchronously before any hooks to ensure consistent useTranslation behavior
  initializeI18n(language, customTranslations)

  const [messages, setMessages] = useState<Message[]>([])
  const [relatedEntities, setRelatedEntities] = useState<RelatedEntity[]>([])
  const [selectedEntities, setSelectedEntities] = useState<Set<string>>(new Set())
  const [newMessage, setNewMessage] = useState("")
  const [newMessageIsImportant, setNewMessageIsImportant] = useState(false)
  const [messageTab, setMessageTab] = useState<"internal" | "client" | "all">("internal")
  // Whether a CLIENT participant is on the current thread — reported up from the participants list, an
  // ORGANIZATIONAL profile specifically (a worker is also non-internal but does not count). Starts false
  // so a thread never flashes an open client lane before the participants load.
  const [hasClientParticipant, setHasClientParticipant] = useState(false)

  // The Client/All tabs open when a client participant is on the thread (so an internal user can START a
  // client conversation) OR the current view already holds a client-visible message. The second arm keeps
  // a related thread's client message reachable rather than hidden behind a greyed tab — e.g. a WorkOrder
  // showing its linked Request's client message under "All related".
  //
  // Scoped to `selectedEntities`, NOT the raw `messages`: on a thread switch `messages` still holds the
  // previous thread's rows until the async refetch lands, so an unscoped check would briefly re-open the
  // tab from stale data. `selectedEntities` updates synchronously with the switch (and includes the
  // related entities in "All related"), so the stale rows fall out while the related ones still count.
  const clientAccessEnabled =
    hasClientParticipant ||
    messages.some(
      message =>
        message.isVisibleToClient &&
        selectedEntities.has(`${message.entityTypeId}:${message.entityId}`)
    )
  const [showLoading, setShowLoading] = useState(false)
  const [showImportantOnly, setShowImportantOnly] = useState(false)
  const [showUnreadOnly, setShowUnreadOnly] = useState(false)

  const [user, setUser] = useState<User | null>(null)
  const [activeReplyIds, setActiveReplyIds] = useState<Set<string>>(new Set())
  const [replyMessages, setReplyMessages] = useState<Map<string, string>>(new Map())
  const [replyImportance, setReplyImportance] = useState<Map<string, boolean>>(new Map())
  const [messageCounts, setMessageCounts] = useState({ internal: 0, client: 0, all: 0 })
  const [showScrollToBottom, setShowScrollToBottom] = useState(false)
  const [isSendingReply, setIsSendingReply] = useState(false)

  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const lastReportedCountRef = useRef<{ entityId: string; count: number } | null>(null)

  const htmlEditorRef = useRef<TiptapHtmlEditorRef>(null)
  const replyHtmlEditorRef = useRef<TiptapHtmlEditorRef>(null)
  const { config } = useMessageComponents()
  const { userToken, sx: providerSx } = config
  const { t } = useTranslation()

  // Merge provider sx with component sx (component sx has higher priority)
  const mergedSx = useMemo(() => mergeSx(providerSx, sx), [providerSx, sx])

  // Get API service instance
  const apiService = useMessageApi()

  // Current entity key for comparison
  const currentEntityKey = `${entityTypeId}:${entityId}`

  // Helper function to update reply message for specific message ID
  const updateReplyMessage = useCallback((messageId: string, content: string) => {
    setReplyMessages(prev => {
      const newMap = new Map(prev)
      if (content.trim()) {
        newMap.set(messageId, content)
      } else {
        newMap.delete(messageId)
      }
      return newMap
    })
  }, [])

  // Helper function to update reply importance for specific message ID
  const updateReplyImportance = useCallback((messageId: string, isImportant: boolean) => {
    setReplyImportance(prev => {
      const newMap = new Map(prev)
      newMap.set(messageId, isImportant)
      return newMap
    })
  }, [])

  // Helper function to check if user is at bottom of messages
  const isAtBottom = useCallback(() => {
    const container = messagesContainerRef.current
    if (!container) return true

    const threshold = 100 // pixels from bottom to consider "at bottom"
    return container.scrollHeight - container.scrollTop - container.clientHeight <= threshold
  }, [])

  // Helper function to scroll to bottom
  const scrollToBottom = useCallback(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      })
    }
  }, [])

  // Generate CSS styles for popover elements that can be applied globally
  const generatePopoverStyles = (mergedSx: any, instanceClass: string): string => {
    if (!mergedSx) return ''

    let css = ''

    // Extract and apply popover-specific styles
    const popoverSelectors = [
      '.entity-chat-panel-filter-popover.entity-chat-ui-popover-content',
      '.entity-chat-panel-filter-title',
      '.entity-chat-panel-filter-description',
      '.entity-chat-panel-filter-option-text',
      '.entity-chat-panel-filter-option'
    ]

    for (const selector of popoverSelectors) {
      const styles = mergedSx[`& ${selector}`]
      if (styles && typeof styles === 'object') {
        // Handle popover container differently - it has the data attribute itself
        if (selector === '.entity-chat-panel-filter-popover.entity-chat-ui-popover-content') {
          css += `[data-theme-instance="${instanceClass}"]${selector} {\n`
        } else {
          // Generate CSS for child elements using descendant selector
          css += `[data-theme-instance="${instanceClass}"] ${selector} {\n`
        }

        for (const [key, value] of Object.entries(styles)) {
          if (typeof value !== 'object') {
            const cssProperty = camelToKebab(key)
            // Remove !important from values since we're using inline styles
            const cleanValue = typeof value === 'string' ? value.replace(' !important', '') : value
            css += `  ${cssProperty}: ${cleanValue} !important;\n`
          } else if (key === '&:hover') {
            // Handle hover states
            css += `}\n[data-theme-instance="${instanceClass}"] ${selector}:hover {\n`
            for (const [hoverKey, hoverValue] of Object.entries(value as any)) {
              const cssProperty = hoverKey.replace(/([A-Z])/g, '-$1').toLowerCase()
              const cleanValue = typeof hoverValue === 'string' ? hoverValue.replace(' !important', '') : hoverValue
              css += `  ${cssProperty}: ${cleanValue} !important;\n`
            }
          }
        }
        css += '}\n'
      }
    }


    return css
  }


  // Fetch user data on mount
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await apiService.fetchUserWithToken()
        setUser(userData)
      } catch (error) {
        // Error is already logged in the service
      }
    }

    if (userToken) {
      fetchUser()
    }
  }, [userToken, apiService])

  // Initialize selected entities when props change
  useEffect(() => {
    setSelectedEntities(new Set([currentEntityKey]))
  }, [currentEntityKey])

  // When showRelatedEntities is false, ensure only current entity is selected
  useEffect(() => {
    if (!showRelatedEntities) {
      setSelectedEntities(new Set([currentEntityKey]))
    }
  }, [showRelatedEntities, currentEntityKey])

  // Track if auto-include has already been applied during this mount cycle
  const autoIncludeAppliedRef = useRef(false)

  // Auto-include related entities of specified types when relatedEntities are fetched (only once per open)
  useEffect(() => {
    if (autoIncludeAppliedRef.current) return
    if (autoIncludeRelatedTypes.length === 0 || relatedEntities.length === 0) return

    const autoIncludeSet = new Set(autoIncludeRelatedTypes)
    const entitiesToAutoInclude = relatedEntities
      .filter((entity) => autoIncludeSet.has(entity.entityTypeId))
      .map((entity) => `${entity.entityTypeId}:${entity.entityId}`)

    if (entitiesToAutoInclude.length > 0) {
      setSelectedEntities((prev) => {
        const newSet = new Set(prev)
        entitiesToAutoInclude.forEach((key) => {
          newSet.add(key)
        })
        return newSet
      })
      autoIncludeAppliedRef.current = true
    }
  }, [relatedEntities, autoIncludeRelatedTypes])

  // Drop back to Internal whenever the client lane is closed — either the surface forces internal-only,
  // or the thread has no client access yet. Keeps the composer off the client lane when the tab it was
  // on becomes unavailable (e.g. switching to a thread a client cannot see).
  useEffect(() => {
    if ((internalOnly || !clientAccessEnabled) && (messageTab === "client" || messageTab === "all")) {
      setMessageTab("internal")
    }
  }, [internalOnly, clientAccessEnabled, messageTab])


  // Handle scroll events to show/hide scroll to bottom button
  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const handleScroll = () => {
      setShowScrollToBottom(!isAtBottom())
    }

    // Check initial state
    handleScroll()

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [isAtBottom])

  // Update scroll to bottom button visibility when messages or filters change
  useEffect(() => {
    // Check immediately - React should have updated the DOM by now
    setShowScrollToBottom(!isAtBottom())
  }, [messages, showImportantOnly, showUnreadOnly, messageTab, isAtBottom])

  // Clear reply sending flag when user changes context (tabs/filters)
  useEffect(() => {
    setIsSendingReply(false)
  }, [messageTab, showImportantOnly, showUnreadOnly])

  // Fetch related entities when component mounts or entity changes
  useEffect(() => {
    if (user && showRelatedEntities) {
      fetchRelatedEntities()
    }
  }, [entityTypeId, entityId, user, showRelatedEntities])

  // Fetch messages when selected entities change
  useEffect(() => {
    if (user) {
      fetchMessages()
    }
  }, [selectedEntities, user])

  // Mark read whatever the reader is actually looking at — which is the current selection, not just
  // the entity the panel was opened on.
  //
  // Each related entity is its own conversation with its own membership document, so picking one in
  // the selector genuinely opens it: its messages render, and leaving its unread standing would show
  // a badge for a thread the user just read. Only entities that have never been selected during this
  // open are left alone. Tracked as a set rather than a single key, so switching back and forth does
  // not re-send.
  //
  // Fire-and-forget by design. This is bookkeeping, not content — a failure must not surface an
  // error over a chat the user opened successfully, and the next open retries it anyway.
  // Join / leave. Open to clients too — muting a thread you are already on is a real thing to want.
  // The narrower rule is enforced server-side: a client may only toggle a conversation whose derived
  // keys already contain them, so they can never grant themselves entity access, and leaving mutes
  // them rather than revoking that key.
  const [isSubscribed, setIsSubscribed] = useState<boolean | null>(initialIsSubscribed ?? null)
  const [isChangingSubscription, setIsChangingSubscription] = useState(false)
  // Rendered whenever there is a viewer; DISABLED (not unmounted) until the subscription state resolves —
  // canManageSubscription gates rendering below, so folding the unknown check into it would make the
  // control vanish and reflow the bar mid-fetch. A control acting on a value we have not resolved would
  // label a muted reader "Mute" and no-op their first click. Resolved immediately in the common case (the
  // trigger seeds it); null only during the off/standalone fetch above.
  const canManageSubscription = Boolean(user)
  const isSubscriptionResolved = isSubscribed !== null

  // The entity the panel is showing RIGHT NOW. The async write handlers (toggleSubscription,
  // toggleMessageRead) capture the entity they fired for and check this before applying local state, so a
  // response landing after the panel is re-pointed at a different entity can't overwrite the new entity's
  // display. The sibling hooks (use-conversation-summary, use-conversation-participants) guard the same way.
  const liveEntityKeyRef = useRef(`${entityTypeId}:${entityId}`)
  // Synced after commit, not during render: a render can be discarded (Suspense, or a concurrent update
  // under a React 18 host), and the async guards below read this — a render-time write could leave it
  // pointing at an entity that never committed. The handlers only read it on user input, always
  // post-commit, so an effect is soon enough.
  useEffect(() => {
    liveEntityKeyRef.current = `${entityTypeId}:${entityId}`
  }, [entityTypeId, entityId])

  // Resolve the subscription state, from the trigger's summary where there is one and by fetching where
  // there is not. `initialIsSubscribed` is undefined in two very different situations, and they need
  // opposite handling:
  //   - defined (true/false): the trigger's summary knows it — use it.
  //   - undefined, SAME entity: a summary refetch failed (load() nulls it) after a local mute write fed
  //     notifyConversationsChanged back through here. Keep the value we have — clobbering it reverts the
  //     write and dead-ends the button.
  //   - undefined, entity CHANGED (summaryMode="off" or a standalone panel with no trigger summary): we
  //     have never known it for this entity, so fetch it rather than showing a muted reader "Mute". The
  //     control stays disabled until it resolves (isSubscriptionResolved).
  const seededSubscriptionEntityRef = useRef<string | null>(null)
  useEffect(() => {
    const key = `${entityTypeId}:${entityId}`
    const entityChanged = seededSubscriptionEntityRef.current !== key

    if (initialIsSubscribed !== undefined) {
      seededSubscriptionEntityRef.current = key
      setIsSubscribed(initialIsSubscribed)
      return
    }
    if (!entityChanged) return

    seededSubscriptionEntityRef.current = key
    setIsSubscribed(null)
    let cancelled = false
    void apiService
      .fetchConversationSummariesWithToken(entityTypeId, [entityId])
      .then(([summary]) => { if (!cancelled) setIsSubscribed(summary?.isSubscribed ?? false) })
      .catch(() => {
        if (cancelled) return
        // Unknown, not "not subscribed" — asserting false would label a subscribed reader "Unmute" and
        // cost them a wasted click. Drop the claim so a later run (entity change, or the trigger's summary
        // arriving) retries rather than leaving the entity permanently unresolved.
        seededSubscriptionEntityRef.current = null
        setIsSubscribed(null)
      })
    return () => {
      cancelled = true
      // Release our claim so StrictMode's dev mount→cleanup→mount re-run refetches: without this the
      // re-run sees the ref already set to `key`, takes the `!entityChanged` early return, and leaves
      // isSubscribed null forever (control disabled) in dev. Only clear OUR claim — a later run for a
      // different entity owns the ref by then. The "keep the value" path returns without a cleanup, so
      // its claim survives a failed summary refetch, which is the whole point of the ref.
      if (seededSubscriptionEntityRef.current === key) seededSubscriptionEntityRef.current = null
    }
  }, [apiService, initialIsSubscribed, entityTypeId, entityId])

  // `isSubscribed` is resolved above (from the summary, or fetched); null now only means "still
  // resolving", during which the button is disabled (isSubscriptionResolved on its `disabled` prop), so a
  // null never reaches an actionable label or the write. The `?? true` on the write below is
  // belt-and-suspenders for that window.
  const isMuted = isSubscribed === false
  // Neutral while resolving: isMuted is false for BOTH "subscribed" and "unknown", so showing the
  // subscribed label ("Mute") for an unknown state asserts a setting we have not fetched — and the button
  // is visible-but-disabled in that window, so a screen reader would still announce it. Show a neutral
  // "Notifications" label/tooltip until isSubscriptionResolved; the icon and the muted class stay as-is
  // (isMuted === false is the right neutral for both).
  const subscriptionLabel = isSubscriptionResolved
    ? (isMuted ? t('subscription.unmute') : t('subscription.mute'))
    : t('subscription.resolving')
  const subscriptionTooltip = isSubscriptionResolved
    ? (isMuted ? t('subscription.unmuteTooltip') : t('subscription.muteTooltip'))
    : t('subscription.resolvingTooltip')

  const toggleSubscription = async () => {
    // The button's `disabled` already blocks this while unresolved, but guard here too so it can never act
    // on a null (unknown) value — the write below assumes a known state.
    if (isChangingSubscription || isSubscribed === null) return
    const key = `${entityTypeId}:${entityId}`
    const next = !isSubscribed

    setIsChangingSubscription(true)
    try {
      await apiService.setConversationSubscription({ entityTypeId, entityId, subscribed: next })
      // Apply only if the panel still shows the entity we wrote for — it may have been re-pointed
      // mid-flight, in which case the seed effect has already set the new entity's state.
      if (liveEntityKeyRef.current === key) setIsSubscribed(next)
      // The row badge derives from the same keys we just changed, so it has to re-read.
      notifyConversationsChanged(entityTypeId, [entityId])
    } catch {
      // Already logged in the service.
    } finally {
      setIsChangingSubscription(false)
    }
  }

  /**
   * Read state for the NEW divider and the per-message toggles.
   *
   * `initialUnreadCount` is a SNAPSHOT taken before the panel opened, and it has to be: opening marks
   * the thread read, so by now the server would report zero. Keeping our own copy is what lets the
   * divider stay put while you read, exactly like every other chat client.
   *
   * Only meaningful for ONE conversation. With several entities selected the panel is showing several
   * watermarks merged and there is no single boundary to draw, so everything below switches off.
   */
  const [unreadCount, setUnreadCount] = useState<number>(initialUnreadCount ?? 0)
  const [isTogglingRead, setIsTogglingRead] = useState(false)

  // Re-snapshot when the panel is pointed at a different entity.
  useEffect(() => {
    setUnreadCount(initialUnreadCount ?? 0)
  }, [initialUnreadCount, entityTypeId, entityId])

  /**
   * Read state is shown only when the one selected entity IS the entity the panel was opened on.
   *
   * `initialUnreadCount` is a snapshot of THAT entity, taken before opening. Selecting a single
   * *related* entity instead used to keep the toggles on, still measuring the anchor: the divider was
   * computed from messages the view no longer showed, and the toggle addressed a conversation the
   * clicked message was not in. The server refused it as NotFound and wrote nothing, so the click
   * silently did nothing while the optimistic update blanked the anchor's divider until the refetch.
   *
   * Hiding it is the honest answer rather than a limitation: we never took a snapshot of that other
   * entity, and re-reading one now would report zero, because opening the panel marked it read.
   */
  const anchorKey = `${entityTypeId}:${entityId}`

  /**
   * Which conversation the participant list is for.
   *
   * Follows the SELECTION, not the anchor. Deselecting the assignment and selecting the work order
   * shows one thread, and it is not the one the panel was opened on — a participants pill still
   * reading "Assignment participants" there is describing a conversation that is not on screen.
   *
   * Null with several selected, because participants belong to one conversation and a merged list
   * would offer "remove" buttons that each act on a different thread.
   */
  const participantsEntity = useMemo(() => {
    const key = selectedEntities.size === 1 ? Array.from(selectedEntities)[0] : anchorKey
    const separator = key.indexOf(':')
    return { entityTypeId: key.slice(0, separator), entityId: key.slice(separator + 1) }
  }, [selectedEntities, anchorKey])

  // Close the client lane whenever the current thread changes; the participants list reports the new
  // value once it loads. Without this, switching from a client-facing thread to an internal one would
  // briefly keep the Client tab open. Also covers the multi-select case, where no single participants
  // list is shown to report access.
  useEffect(() => {
    setHasClientParticipant(false)
  }, [participantsEntity.entityTypeId, participantsEntity.entityId])

  // Adding somebody acts on ONE thread. With several selected there is no single answer to "which",
  // so the list still shows the record the panel was opened on — seeing who is on it is useful in
  // every scope — and only the action is withheld.
  const canManageParticipants = selectedEntities.size === 1
  const showReadState =
    initialUnreadCount !== undefined &&
    selectedEntities.size === 1 &&
    selectedEntities.has(anchorKey)

  const currentEntityMessages = useMemo(
    () => messages.filter(m => m.entityTypeId === entityTypeId && m.entityId === entityId),
    [messages, entityTypeId, entityId]
  )

  /**
   * How many messages were unread on each entity when the panel opened.
   *
   * First value seen per entity wins and is never lowered, exactly like `initialUnreadCount`: opening
   * marks every selected entity read, so anything read back afterwards reports zero. The related
   * entities arrive from their own fetch, which races that mark-read — without the first-wins rule the
   * highlight would appear and then vanish depending on which landed first.
   *
   * Reset when the panel is pointed at a different record, because the snapshot describes one visit.
   */
  const unreadSnapshotRef = useRef<Record<string, number>>({})
  // Where the NEW divider landed, one boundary per audience. Captured the first time a visit sees an
  // unread message on that tab and then held — mark-read-on-open (and the summary refetch it triggers)
  // drops the live unread to zero, and without this the line would vanish the moment it did its job.
  const firstUnreadSnapshotRef = useRef<{ internal: string | null; client: string | null }>({
    internal: null,
    client: null
  })
  useEffect(() => {
    unreadSnapshotRef.current = {}
    firstUnreadSnapshotRef.current = { internal: null, client: null }
    setAudienceUnread(null)
  }, [entityTypeId, entityId])

  const [unreadSnapshotVersion, setUnreadSnapshotVersion] = useState(0)
  const [firstUnreadVersion, setFirstUnreadVersion] = useState(0)
  useEffect(() => {
    const snapshot = unreadSnapshotRef.current
    let changed = false

    const remember = (key: string, count: number | undefined) => {
      if (count === undefined || count <= 0) return
      if ((snapshot[key] ?? 0) >= count) return
      snapshot[key] = count
      changed = true
    }

    remember(anchorKey, initialUnreadCount)
    relatedEntities.forEach(entity =>
      remember(`${entity.entityTypeId}:${entity.entityId}`, entity.unreadCount)
    )

    if (changed) setUnreadSnapshotVersion(version => version + 1)
  }, [anchorKey, initialUnreadCount, relatedEntities])

  /**
   * Which messages are unread, across EVERY selected entity.
   *
   * The NEW divider still needs one conversation — a merged view has no single boundary to draw — but
   * "which of these did I not read" is answerable per thread and then unioned. Without this, switching
   * to All related silently dropped the highlighting even though the panel had just marked those
   * threads read on your behalf.
   */
  /**
   * The anchor's two boundaries, one per audience.
   *
   * An internal reader has an independent boundary for internal-only and client-visible messages, so
   * each tab measures its own. Before the split there was one boundary over the combined list, and
   * marking an internal message unread also marked every client message after it — which the Internal
   * tab then rendered as three unread while All said four. Same boundary, two filters, and neither
   * number was explicable from what was on screen.
   */
  const [audienceUnread, setAudienceUnread] = useState<{ internal: number; client: number } | null>(null)


  const unreadMessageIds = useMemo(() => {
    void unreadSnapshotVersion
    const ids = new Set<string>()

    selectedEntities.forEach(key => {
      const separator = key.indexOf(':')
      const typeId = key.slice(0, separator)
      const id = key.slice(separator + 1)
      const entityMessages = messages.filter(m => m.entityTypeId === typeId && m.entityId === id)

      // The anchor carries live per-audience state once anything has been toggled; every other entity
      // keeps the single snapshot it arrived with, because nothing here can change its read state.
      if (key === anchorKey && audienceUnread) {
        resolveUnreadMessageIdsForAudience(entityMessages, audienceUnread.internal, false)
          .forEach(messageId => ids.add(messageId))
        resolveUnreadMessageIdsForAudience(entityMessages, audienceUnread.client, true)
          .forEach(messageId => ids.add(messageId))
        return
      }

      const count = key === anchorKey ? unreadCount : (unreadSnapshotRef.current[key] ?? 0)
      if (count <= 0) return
      resolveUnreadMessageIds(entityMessages, count).forEach(messageId => ids.add(messageId))
    })

    return ids
  }, [selectedEntities, messages, anchorKey, unreadCount, unreadSnapshotVersion, audienceUnread])

  /**
   * Where the NEW divider goes.
   *
   * Only on a single-audience tab. "All" merges two independent boundaries, so there is no one place
   * to draw it — showing either would put a line in the middle of messages on the other side of the
   * other boundary. The per-message highlight still marks everything unread there, which is the
   * honest answer rather than a misplaced line.
   *
   * The line marks where unread began WHEN THE PANEL OPENED and has to stay put while you read, so the
   * effect captures the first unread message on the tab into a per-visit snapshot (first one wins)
   * rather than tracking the live count, which mark-read-on-open drives to zero. It is derived from the
   * same unread set the per-message highlight uses, filtered to the tab's own audience, so the line
   * always lands on a message actually on screen — the old combined-count boundary could resolve to a
   * message on the other tab (or a reply the root-level render never checks) and then draw nothing.
   */
  useEffect(() => {
    if (!showReadState || messageTab === 'all') return
    const lane: 'internal' | 'client' = messageTab === 'client' ? 'client' : 'internal'
    if (firstUnreadSnapshotRef.current[lane]) return

    const wantsClient = lane === 'client'
    const firstUnread = currentEntityMessages.find(
      message => message.isVisibleToClient === wantsClient && unreadMessageIds.has(message.id)
    )
    if (firstUnread) {
      firstUnreadSnapshotRef.current[lane] = firstUnread.id
      setFirstUnreadVersion(version => version + 1)
    }
  }, [showReadState, messageTab, currentEntityMessages, unreadMessageIds])

  const firstUnreadId = useMemo(() => {
    void firstUnreadVersion
    if (!showReadState || messageTab === 'all') return null
    const lane: 'internal' | 'client' = messageTab === 'client' ? 'client' : 'internal'
    return firstUnreadSnapshotRef.current[lane]
  }, [showReadState, messageTab, firstUnreadVersion])

  /** Mark-read-on-open calls still in flight, so an explicit toggle can order itself after them. */
  const markReadInFlightRef = useRef<Promise<unknown>[]>([])

  /**
   * Toggle read state at one message.
   *
   * The server treats this as a slider, not a per-message flag: marking read covers that message and
   * everything older, marking unread stops just before it. `unreadCountAfterToggle` mirrors those
   * rules so the optimistic update and any later refetch cannot disagree.
   */
  const toggleMessageRead = useCallback(
    async (message: Message, read: boolean) => {
      if (isTogglingRead) return

      // Address the message's OWN conversation. `showReadState` currently guarantees this equals the
      // anchor, but reading it off the message is what keeps that true: the server resolves
      // `atMessageId` inside the target conversation and answers NotFound — silently, by design, so it
      // cannot be used to probe for message ids — if the two ever drift apart again.
      const targetTypeId = message.entityTypeId
      const targetId = message.entityId
      const targetKey = `${targetTypeId}:${targetId}`

      // Capture the suppression state BEFORE the optimistic flip so a failed request can restore exactly
      // that. suppress/allow are idempotent Set add/delete, not a boolean toggle, so restoring the
      // inverse of the post-call state would (e.g.) add a suppression key to a thread that never had one.
      const wasSuppressed = isMarkReadSuppressedKey(targetKey)

      // Recorded before the request, not after: an opener already in flight must not be able to win
      // a race whose outcome the reader has just decided. Marking read again is the only thing that
      // lifts it.
      if (read) {
        allowMarkRead(targetTypeId, targetId)
      } else {
        suppressMarkRead(targetTypeId, targetId)
      }

      const previous = unreadCount
      const nextUnread = unreadCountAfterToggle(currentEntityMessages, message.id, read)
      setUnreadCount(nextUnread)
      setIsTogglingRead(true)

      try {
        // Wait out mark-read-on-open before writing.
        //
        // That call is deliberately fire-and-forget, so opening a thread and immediately marking a
        // message unread raced it: two writes to the same watermark with no ordering, and when the
        // opener landed second the unread was silently undone. The user saw the badge come back for a
        // moment and then clear itself, and "it only marks unread on the second attempt" — the second
        // attempt works because by then the opener has finished.
        // Ordering here rather than cancelling: the mark-read still has to happen, it just has to
        // happen first.
        if (markReadInFlightRef.current.length > 0) {
          await Promise.allSettled(markReadInFlightRef.current)
          markReadInFlightRef.current = []
        }

        const result = await apiService.markConversationRead({
          entityTypeId: targetTypeId,
          read,
          items: [{ entityId: targetId, atMessageId: message.id }]
        })

        // Take the server's answer over our own estimate.
        //
        // The estimate is made from the messages this panel happens to have rendered; the arithmetic
        // ran against the server's ordered list. Whenever those differ — a reply ordered by its own
        // timestamp, a message outside the current filter — the counts disagree by one, and the
        // message you just clicked ends up on the wrong side of the boundary. That was the
        // "marks everything below but not the one I picked" bug.
        const serverState = result?.states?.find(state => state.entityId === targetId)

        // markConversationRead records HTTP/server failures in `result.errors` instead of throwing (it
        // chunks and must not discard a partial aggregate). A failed single-item write leaves no
        // serverState, so committing here would stamp the OPTIMISTIC count, keep the suppression flip,
        // and notify the trigger as if the server had agreed. Treat it as a failure so the catch rolls
        // everything back.
        if (result.errors.length > 0 || !serverState) {
          throw new Error(result.errors[0] ?? "Conversation read state was not updated")
        }

        const settledUnread = serverState?.unreadCount ?? nextUnread

        // Keep the lanes apart for the anchor — the badge is their sum, but each tab draws its own
        // boundary and cannot do that from a total.
        if (
          liveEntityKeyRef.current === targetKey &&
          serverState?.internalUnreadCount !== undefined &&
          serverState?.clientUnreadCount !== undefined
        ) {
          setAudienceUnread({
            internal: serverState.internalUnreadCount,
            client: serverState.clientUnreadCount
          })
        }
        const settledImportant = serverState?.hasImportantUnread ?? (!read && message.isImportant === true)

        if (liveEntityKeyRef.current === targetKey) {
          setUnreadCount(settledUnread)
        }
        unreadSnapshotRef.current[`${targetTypeId}:${targetId}`] = settledUnread
        setUnreadSnapshotVersion(version => version + 1)

        notifyConversationsChanged(targetTypeId, [targetId])

        // Tell the trigger what this thread actually ended up at. Without it, closing the panel wipes
        // the badge unconditionally — so marking something unread and closing left the grid showing
        // nothing, which is precisely the thing the feature exists to prevent. Guarded like the two
        // setUnreadCount writes: this flows into the trigger's summary (not keyed to an entity) and is
        // replayed on close, so firing it after the panel is re-pointed would stamp this thread's count
        // onto the wrong badge and closing would re-assert it.
        if (liveEntityKeyRef.current === targetKey) {
          onUnreadChange?.(settledUnread, settledImportant)
        }
      } catch {
        // The server did not apply the change, so undo the optimistic suppression flip too — not just
        // the count. Restore the state captured BEFORE the flip (not the inverse of the post-call
        // state): a failed "mark read" on a thread that was never suppressed must NOT add a suppression
        // key, which would block mark-read-on-open for the rest of the session.
        if (wasSuppressed) {
          suppressMarkRead(targetTypeId, targetId)
        } else {
          allowMarkRead(targetTypeId, targetId)
        }
        // Same live-entity guard as the success paths: `previous` is the count from the entity showing
        // when the toggle fired, so rolling it back after the panel was re-pointed would write a
        // known-stale value onto the new entity.
        if (liveEntityKeyRef.current === targetKey) setUnreadCount(previous)
      } finally {
        setIsTogglingRead(false)
      }
    },
    [isTogglingRead, unreadCount, currentEntityMessages, apiService, onUnreadChange]
  )

  const markedReadRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!user || !markReadOnOpen) return

    const unmarked = Array.from(selectedEntities).filter((key) => {
      if (markedReadRef.current.has(key)) return false
      // Explicitly marked unread by this reader. The per-mount ref above cannot cover this: closing
      // the panel and having anything re-render the trigger remounts it, and the effect then marked
      // the thread read again — which is the unread "disappearing after a minute".
      if (isMarkReadSuppressedKey(key)) return false
      // The trigger already knows this one has nothing to clear — its summary said so. Skipping it
      // here is the difference between opening a thread you do not participate in costing zero
      // requests and costing one the server only no-ops.
      return !(entityAlreadyRead && key === `${entityTypeId}:${entityId}`)
    })
    if (unmarked.length === 0) return

    // One call per entity, deliberately NOT batched per entity type. The server's access gate is
    // all-or-nothing: a single related entity the caller cannot reach would 403 the whole batch and
    // leave every other entity unread — and because this is fire-and-forget, nobody would ever find
    // out. N is one to three here, so the extra calls cost nothing worth protecting.
    unmarked.forEach((key) => {
      markedReadRef.current.add(key)
      const separator = key.indexOf(':')
      const typeId = key.slice(0, separator)
      const id = key.slice(separator + 1)

      const pending = apiService.markConversationReadWithToken(typeId, id).catch(() => {
        // Already logged in the service.
      })
      markReadInFlightRef.current.push(pending)
      // Drop it once settled so the list cannot grow with the length of the session.
      void pending.finally(() => {
        markReadInFlightRef.current = markReadInFlightRef.current.filter(entry => entry !== pending)
      })
    })
  }, [user, markReadOnOpen, entityAlreadyRead, selectedEntities, entityTypeId, entityId, apiService])

  async function fetchRelatedEntities() {
    if (!user) return

    try {
      const relatedEntitiesData = await apiService.fetchRelatedEntitiesWithToken(
        entityTypeId,
        entityId
      )
      setRelatedEntities(relatedEntitiesData)
    } catch (error) {
      // Error is already logged in the service
    }
  }

  async function fetchMessages() {
    if (!user) return

    // Handle case where no entities are selected
    if (selectedEntities.size === 0) {
      setMessages([]) // Clear messages when no entities selected

      // Still update the message count for the current entity even when no entities are selected
      if (onMessageCountChanged) {
        try {
          const count = await apiService.fetchMessageCountWithToken(entityTypeId, entityId)
          const last = lastReportedCountRef.current
          if (last === null || last.entityId !== entityId || last.count !== count) {
            lastReportedCountRef.current = { entityId, count }
            onMessageCountChanged(count)
          }
        } catch (error) {
          // Error already logged in service
        }
      }
      return
    }

    // Only show loading indicator after a delay to prevent flash
    const loadingTimeout = setTimeout(() => {
      setShowLoading(true)
    }, 200)

    try {
      const entities = Array.from(selectedEntities)
        .map((entityKey) => {
          const [typeId, id] = entityKey.split(":")
          return { entityTypeId: typeId, entityId: id }
        })

      const messagesData = await apiService.fetchMessagesWithToken(entities)
      setMessages(messagesData)

      // Always update trigger with accurate message count for the current entity
      // Use dedicated API call to ensure we get the correct count regardless of panel filter state
      if (onMessageCountChanged) {
        try {
          const count = await apiService.fetchMessageCountWithToken(entityTypeId, entityId)
          const last = lastReportedCountRef.current
          if (last === null || last.entityId !== entityId || last.count !== count) {
            lastReportedCountRef.current = { entityId, count }
            onMessageCountChanged(count)
          }
        } catch (error) {
          // Error already logged in service
        }
      }
    } catch (error) {
      // Error is already logged in the service
    } finally {
      clearTimeout(loadingTimeout)
      setShowLoading(false)
    }
  }

  const sendMessage = useCallback(async () => {
    if (!newMessage.trim() || messageTab === "all" || !user) return

    // Always send to the current entity only
    const messageData = {
      content: newMessage,
      contentType: "html" as const, // DevExtreme HTML Editor always produces HTML
      entityTypeId,
      entityId,
      isVisibleToClient: user.type === "client" ? true : messageTab === "client",
      isImportant: newMessageIsImportant,
    }

    try {
      // Clear reply flag to ensure normal auto-scroll behavior for new messages
      setIsSendingReply(false)

      await apiService.sendMessageWithToken(messageData)
      // Invalidate the row summary so the grid cell reflects the new message (a hollow "no messages"
      // bubble becomes a read bubble) — the panel's local fetchMessages never touches the trigger's
      // cached summary, so without this the cell stays empty until a full reload.
      notifyConversationsChanged(entityTypeId, [entityId])
      // Refresh messages to get the threaded structure
      const promises = [fetchMessages()]
      // Refresh related entities to update message counts in dropdown
      if (showRelatedEntities) {
        promises.push(fetchRelatedEntities())
      }
      await Promise.all(promises)
      setNewMessage("")
      setNewMessageIsImportant(false)
      // Clear the HTML editor as well
      htmlEditorRef.current?.clear()
    } catch (error) {
      // Error is already logged in the service
    }
  }, [newMessage, newMessageIsImportant, messageTab, user, entityTypeId, entityId, apiService, fetchMessages, fetchRelatedEntities])

  const sendReply = async (parentId: string) => {
    const replyContent = replyMessages.get(parentId) || ""
    if (!replyContent.trim() || messageTab === "all" || !user) return

    const messageData = {
      content: replyContent,
      contentType: "html" as const, // DevExtreme HTML Editor always produces HTML
      entityTypeId,
      entityId,
      isVisibleToClient: user.type === "client" ? true : messageTab === "client",
      isImportant: replyImportance.get(parentId) || false,
      parentId,
    }

    try {
      // Set flag to prevent auto-scroll to bottom during reply sending
      setIsSendingReply(true)

      await apiService.sendMessageWithToken(messageData)
      // Invalidate the row summary so the grid cell reflects the new reply (see sendMessage).
      notifyConversationsChanged(entityTypeId, [entityId])
      // Clear state immediately, then refresh in parallel
      setReplyMessages(prev => {
        const newMap = new Map(prev)
        newMap.delete(parentId)
        return newMap
      })
      setReplyImportance(prev => {
        const newMap = new Map(prev)
        newMap.delete(parentId)
        return newMap
      })
      setActiveReplyIds(prev => {
        const newSet = new Set(prev)
        newSet.delete(parentId)
        return newSet
      })
      replyHtmlEditorRef.current?.clear()
      const promises: Promise<void>[] = [fetchMessages()]
      if (showRelatedEntities) promises.push(fetchRelatedEntities())
      await Promise.all(promises)

      // Scroll to show the new reply at the bottom of the parent message thread
      setTimeout(() => {
        // Find the parent message thread container
        const parentMessageElement = document.querySelector(`[data-message-id="${parentId}"]`)
        if (parentMessageElement && messagesContainerRef.current) {
          const container = messagesContainerRef.current

          // Find the bottom of the entire thread (parent + all replies)
          const threadBottom = (parentMessageElement as HTMLElement).offsetTop +
            (parentMessageElement as HTMLElement).offsetHeight

          // Scroll to show the bottom of the thread (where the new reply is)
          // with some padding so the new reply is comfortably visible
          container.scrollTo({
            top: Math.max(0, threadBottom - container.clientHeight + 100), // 100px padding from bottom
            behavior: 'smooth'
          })
        }

        // Clear the flag after scrolling is set up
        setIsSendingReply(false)
      }, 200)
    } catch (error) {
      // Error is already logged in the service
      setIsSendingReply(false) // Clear flag on error too
    }
  }

  const handleStartReply = (messageId: string) => {
    setActiveReplyIds(prev => new Set(prev).add(messageId))
    // Focus the reply editor and scroll into view after it renders
    setTimeout(() => {
      replyHtmlEditorRef.current?.focus()

      // Scroll the reply area into view (find the reply input area for this specific message)
      const replyInputArea = document.querySelector(`[data-replying-to="${messageId}"]`)
      if (replyInputArea && messagesContainerRef.current) {
        // Use container scrolling for both popup and embedded contexts
        const container = messagesContainerRef.current
        const containerRect = container.getBoundingClientRect()
        const elementRect = replyInputArea.getBoundingClientRect()

        // Check if element is already visible in container
        const isVisible = elementRect.top >= containerRect.top && elementRect.bottom <= containerRect.bottom

        if (!isVisible) {
          const elementOffsetTop = (replyInputArea as HTMLElement).offsetTop - container.offsetTop
          container.scrollTo({
            top: elementOffsetTop - 100, // 100px padding from top
            behavior: 'smooth'
          })
        }
      }
    }, 150)
  }

  const handleCancelReply = (messageId: string) => {
    // Remove this message from active replies
    setActiveReplyIds(prev => {
      const newSet = new Set(prev)
      newSet.delete(messageId)
      return newSet
    })
    // Clear the specific reply message
    setReplyMessages(prev => {
      const newMap = new Map(prev)
      newMap.delete(messageId)
      return newMap
    })
    // Clear the reply importance
    setReplyImportance(prev => {
      const newMap = new Map(prev)
      newMap.delete(messageId)
      return newMap
    })
    // Note: Don't clear replyHtmlEditorRef as it's shared across all editors
    // The content is managed through the replyMessages Map state
  }



  const handleEntityToggle = (entityType: string) => {
    const newSelected = new Set(selectedEntities)

    // Get all entities of this type
    const entitiesOfType = relatedEntities.filter(entity => entity.entityTypeId === entityType)
    const entityKeysOfType = entitiesOfType.map(entity => `${entity.entityTypeId}:${entity.entityId}`)

    // Check if any entity of this type is selected
    const hasAnyOfType = entityKeysOfType.some(key => newSelected.has(key))

    if (hasAnyOfType) {
      // Remove all entities of this type, but ensure current entity stays selected
      entityKeysOfType.forEach(key => {
        if (key !== currentEntityKey) {
          newSelected.delete(key)
        }
      })
    } else {
      // Add all entities of this type
      entityKeysOfType.forEach(key => newSelected.add(key))
    }

    setSelectedEntities(newSelected)
  }

  const handleSelectAll = () => {
    const allEntityKeys = relatedEntities.map(entity => `${entity.entityTypeId}:${entity.entityId}`)
    const isAllSelected = allEntityKeys.every(key => selectedEntities.has(key))

    if (isAllSelected) {
      // Instead of deselecting all, default back to just the current entity
      setSelectedEntities(new Set([currentEntityKey]))
    } else {
      // Select all
      setSelectedEntities(new Set(allEntityKeys))
    }
  }

  const filterMessage = (message: Message): boolean => {
    if (!user) return false

    // First filter by user type (client users only see client-visible messages)
    if (user.type === "client") {
      return message.isVisibleToClient
    }

    // For internal users, apply the message tab filter
    if (messageTab === "internal") {
      return !message.isVisibleToClient
    } else if (messageTab === "client") {
      return message.isVisibleToClient
    }

    // "all" - show everything for internal users
    return true
  }

  const filterMessagesRecursively = (messageList: Message[]): Message[] => {
    return messageList
      .filter(filterMessage)
      .map(message => ({
        ...message,
        replies: message.replies ? filterMessagesRecursively(message.replies) : []
      }))
  }

  // Function to check if a message or any of its replies are important
  const hasImportantMessage = (message: Message): boolean => {
    if (message.isImportant) return true
    if (message.replies && message.replies.length > 0) {
      return message.replies.some(reply => hasImportantMessage(reply))
    }
    return false
  }

  // Enhanced filtering function that handles importance filter with thread hierarchy
  const filterWithImportance = (messageList: Message[]): Message[] => {
    // First apply regular message type filtering (internal/client/all)
    const typeFiltered = messageList
      .filter(filterMessage)
      .map(message => ({
        ...message,
        replies: message.replies ? filterMessagesRecursively(message.replies) : []
      }))

    // Unread narrows to the messages past the watermark. A thread is kept when ANY message in it is
    // unread, the same rule the importance filter uses — dropping a read root would orphan an unread
    // reply and the count would stop matching what is on screen.
    const unreadFiltered = showUnreadOnly
      ? typeFiltered.filter(message => hasUnreadMessage(message))
      : typeFiltered

    // If importance filter is not active, return what we have
    if (!showImportantOnly) {
      return unreadFiltered
    }

    // Apply importance filtering with thread hierarchy
    return unreadFiltered
      .filter(message => hasImportantMessage(message))
      .map(message => ({
        ...message,
        replies: message.replies ? filterWithImportance(message.replies) : []
      }))
  }

  /**
   * Whether a thread contains anything unread. Walks replies, because the watermark counts every
   * message as it arrived rather than only roots.
   */
  const hasUnreadMessage = (message: Message): boolean =>
    unreadMessageIds.has(message.id) ||
    (message.replies?.some(reply => hasUnreadMessage(reply)) ?? false)

  // Helper function to count all messages including replies
  const countMessagesRecursively = (messageList: Message[]): number => {
    let count = 0
    messageList.forEach(message => {
      count += 1 // Count the message itself
      if (message.replies && message.replies.length > 0) {
        count += countMessagesRecursively(message.replies) // Count replies recursively
      }
    })
    return count
  }

  // Calculate tab-specific message counts
  const calculateMessageCounts = (allMessages: Message[]) => {
    if (!user) return { internal: 0, client: 0, all: 0 }

    const counts = { internal: 0, client: 0, all: 0 }

    // Count all messages including replies
    counts.all = countMessagesRecursively(allMessages)

    // For internal users, calculate internal and client counts
    if (user.type === 'internal') {
      // Counted over the FLATTENED thread, not its roots.
      //
      // Filtering roots dropped a whole thread — replies included — whenever the root's audience
      // differed from the one being counted. So a client reply under an internal root was invisible to
      // the Client count and an internal reply under a client root was invisible to Internal, and the
      // two tabs stopped summing to All. Same mistake as the unread lanes: a reply has its own
      // audience and its own place in time, and belongs to whichever tab matches it.
      const countByVisibility = (visible: boolean) =>
        flattenChronologically(allMessages).filter(msg => msg.isVisibleToClient === visible).length
      counts.internal = countByVisibility(false)
      counts.client = countByVisibility(true)
    } else {
      // For client users, only client messages are visible
      counts.client = counts.all
      counts.internal = 0
    }

    return counts
  }

  const filteredMessages = filterWithImportance(messages)

  // Track when we should scroll to bottom
  const shouldScrollToBottom = useRef(false)

  // Set flag when filtered messages change
  useEffect(() => {
    if (isSendingReply) return
    if (filteredMessages.length > 0) {
      shouldScrollToBottom.current = true
    }
  }, [filteredMessages.length, messageTab, showImportantOnly, showUnreadOnly, isSendingReply])

  // Function to attempt scrolling (reusable for both callback ref and visibility change)
  const attemptScroll = useCallback(() => {
    if (!messagesContainerRef.current || !shouldScrollToBottom.current) return

    // Check if container is actually visible and has dimensions
    const rect = messagesContainerRef.current.getBoundingClientRect()
    const hasValidDimensions = rect.width > 0 && rect.height > 0
    const hasValidScrollHeight = messagesContainerRef.current.scrollHeight > 0

    if (hasValidDimensions && hasValidScrollHeight) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      })
      shouldScrollToBottom.current = false
      return true // Successfully scrolled
    }

    return false // Couldn't scroll yet
  }, [])

  // Callback ref for when the last message is rendered
  const lastMessageRef = useCallback((node: HTMLDivElement | null) => {
    if (node && shouldScrollToBottom.current) {
      attemptScroll()
    }
  }, [attemptScroll])

  // Track previous dimensions to detect hidden -> visible transitions
  const previousDimensionsRef = useRef<{ width: number; height: number }>({ width: 0, height: 0 })

  // ResizeObserver to detect when the container becomes visible (not just resized)
  useEffect(() => {
    const container = messagesContainerRef.current
    if (!container) return

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry && shouldScrollToBottom.current) {
        const { width, height } = entry.contentRect
        const prevDimensions = previousDimensionsRef.current

        // Only scroll when transitioning from invalid (0) to valid dimensions (hidden -> visible)
        // Don't scroll on normal resizing (valid -> different valid dimensions)
        const wasHidden = prevDimensions.width === 0 || prevDimensions.height === 0
        const isNowVisible = width > 0 && height > 0

        if (wasHidden && isNowVisible) {
          // Container became visible, try to scroll
          setTimeout(() => attemptScroll(), 100)
        }

        // Update previous dimensions
        previousDimensionsRef.current = { width, height }
      }
    })

    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [attemptScroll])

  // Fallback: Also add a visibility change listener for when the entire document becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && shouldScrollToBottom.current) {
        // Document became visible (user switched back to tab), try to scroll
        setTimeout(() => attemptScroll(), 150)
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [attemptScroll])

  // Update internal message counts for tab badges when messages change
  useEffect(() => {
    const counts = calculateMessageCounts(messages)
    setMessageCounts(counts)
  }, [messages, user])

  const getRelativeTime = (date: Date | string) => {
    const now = new Date()
    const messageDate = new Date(date)
    const diffInMs = now.getTime() - messageDate.getTime()
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))
    const diffInMonths = Math.floor(diffInDays / 30)
    const diffInYears = Math.floor(diffInDays / 365)

    if (diffInMinutes < 1) {
      return t('time.justNow')
    } else if (diffInMinutes < 60) {
      return diffInMinutes === 1 ? t('time.minuteAgo', { count: diffInMinutes }) : t('time.minutesAgo', { count: diffInMinutes })
    } else if (diffInHours < 24) {
      return diffInHours === 1 ? t('time.hourAgo', { count: diffInHours }) : t('time.hoursAgo', { count: diffInHours })
    } else if (diffInDays < 30) {
      return diffInDays === 1 ? t('time.dayAgo', { count: diffInDays }) : t('time.daysAgo', { count: diffInDays })
    } else if (diffInMonths < 12) {
      return diffInMonths === 1 ? t('time.monthAgo', { count: diffInMonths }) : t('time.monthsAgo', { count: diffInMonths })
    } else {
      return diffInYears === 1 ? t('time.yearAgo', { count: diffInYears }) : t('time.yearsAgo', { count: diffInYears })
    }
  }

  const exactTimestampFormatter = useMemo(
    () => new Intl.DateTimeFormat(language || 'en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
    }),
    [language]
  )
  const getExactTimestamp = (date: Date | string) => exactTimestampFormatter.format(new Date(date))

  const formatEntityType = (entityTypeId: string) => {
    return ENTITY_TYPE_LABELS[entityTypeId] ?? entityTypeId.replace(/([A-Z])/g, ' $1').trim()
  }

  // Friendly identifiers supplied by the related-entities endpoint (e.g. ATS placement id,
  // VMS external request id) — used instead of the raw (often GUID) entityId on message pills.
  const entityDisplayIds = useMemo(() => {
    const map = new Map<string, string>()
    for (const entity of relatedEntities) {
      if (entity.displayName) map.set(`${entity.entityTypeId}:${entity.entityId}`, entity.displayName)
    }
    return map
  }, [relatedEntities])

  const formatEntityBadge = useCallback((entityTypeId: string, entityId: string) => {
    const displayId = entityDisplayIds.get(`${entityTypeId}:${entityId}`)
    return displayId
      ? `${formatEntityType(entityTypeId)} - ${displayId}`
      : `${formatEntityType(entityTypeId)} ${entityId}`
  }, [entityDisplayIds])

  // Delegates rather than duplicating. This was the third copy of the same idea, and the three did
  // not agree — see initialsFrom. Still threaded as a prop; unthreading it is separate cleanup.
  const getInitials = (name: string) => initialsFrom(name)

  const selectedCount = selectedEntities.size
  const allEntityKeys = relatedEntities.map(entity => `${entity.entityTypeId}:${entity.entityId}`)
  // For single entity case, "All" should be checked when that single entity is selected
  const isAllSelected = allEntityKeys.length > 0 && allEntityKeys.every(key => selectedEntities.has(key))
  const hasCurrentEntitySelected = selectedEntities.has(currentEntityKey)
  const messageCount = filteredMessages.length

  // Get display text for dropdown trigger
  /**
   * The scope switcher's label. Says what you are LOOKING AT, not which entity types happen to be
   * ticked — "This record" and "All related" are the two answers people actually want, and the type
   * list is detail that belongs inside the popover where the checkboxes are.
   *
   * Deliberately generic rather than naming the entity type. This panel opens on work orders,
   * assignments, organizations and requests alike, so "This request" would be wrong more often than
   * right, and per-type wording multiplies across two languages for no gain.
   */
  const getDropdownDisplayText = () => {
    if (selectedCount === 0) {
      return t('filters.selectEntitiesPlaceholder')
    }

    // Name the record rather than calling it "this record". With one entity selected the panel is
    // showing exactly one thread, and saying which is the difference between the scope switcher being
    // a label and being a statement of what you are looking at.
    if (selectedCount === 1) {
      const [onlyTypeId] = Array.from(selectedEntities)[0].split(':')
      return formatEntityType(onlyTypeId)
    }

    if (relatedEntities.length > 0 && selectedCount === relatedEntities.length) {
      return t('filters.allRelated')
    }

    return t('filters.someSelected', { count: selectedCount })
  }

  // Show panel even when user is loading, but hide user-dependent content

  // Use passed className or generate stable class for this instance
  const instanceClass = className || `entity-chat-panel-${entityTypeId}-${entityId}`

  return (
    <div
      className={cn("entity-chat-panel-container", instanceClass)}
      style={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      {/* Generate CSS for nested selectors */}
      {mergedSx && (
        <style dangerouslySetInnerHTML={{
          __html: sxToStyleTag(mergedSx, instanceClass)
        }} />
      )}

      <TooltipProvider>
        {/* Chat Panel Content */}
        <div
          className={cn("entity-chat-panel", instanceClass)}
          style={{
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden' // Ensure scrolling stays within this container
          }}
        >
          {/* Header */}
          <div
            className="entity-chat-panel-header"
            style={{
              flexShrink: 0,
              padding: '16px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: showCloseButton ? 'space-between' : 'flex-start'
            }}
          >
            <div className="entity-chat-panel-identity">
              <span
                className="entity-chat-panel-header-avatar"
                aria-hidden="true"
                style={headerAvatarColor ? { backgroundColor: headerAvatarColor } : undefined}
              >
                {headerInitials || initialsFrom(title || formatEntityType(entityTypeId))}
              </span>
              <span className="entity-chat-panel-identity-text">
                <h1 className="entity-chat-panel-title">
                  {title || `${formatEntityType(entityTypeId)} ${entityId}`}
                </h1>
                {(headerRecordLabel || headerSubtitle) && (
                  <span className="entity-chat-panel-subtitle">
                    {headerRecordLabel && (
                      headerRecordHref ? (
                        <a
                          className="entity-chat-panel-subtitle-link"
                          href={headerRecordHref}
                          onClick={event => event.stopPropagation()}
                        >
                          {headerRecordLabel}
                        </a>
                      ) : (
                        <span className="entity-chat-panel-subtitle-record">{headerRecordLabel}</span>
                      )
                    )}
                    {/* The separator belongs to the second half, so a record with no subtitle does not
                        render a dangling dot. */}
                    {headerRecordLabel && headerSubtitle && ' · '}
                    {headerSubtitle}
                  </span>
                )}
              </span>
            </div>
            {showCloseButton && (
              <Button variant="ghost" size="icon" onClick={onClose} className="entity-chat-panel-close-button">
                <X className="entity-chat-panel-close-icon" />
              </Button>
            )}
          </div>

          {/* Message Type Tabs - only for internal users */}
          {user && user.type === "internal" && (
            <div
              className="entity-chat-panel-tabs-container">
              <Tabs value={messageTab} onValueChange={(value) => setMessageTab(value as "internal" | "client" | "all")}>
                <TabsList className="entity-chat-panel-tabs-list">
                  <TabsTrigger value="internal" className="entity-chat-panel-tab entity-chat-panel-tab-internal">
                    <Lock className="entity-chat-panel-tab-icon" />
                    {t('tabs.internal')}
                    <Badge variant="secondary" className="entity-chat-panel-tab-count">
                      {messageCounts.internal}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger
                    value="client"
                    className="entity-chat-panel-tab entity-chat-panel-tab-client"
                    disabled={internalOnly || !clientAccessEnabled}
                  >
                    <Eye className="entity-chat-panel-tab-icon" />
                    {t('tabs.client')}
                    <Badge variant="secondary" className="entity-chat-panel-tab-count">
                      {messageCounts.client}
                    </Badge>
                  </TabsTrigger>
                  <TabsTrigger
                    value="all"
                    className="entity-chat-panel-tab entity-chat-panel-tab-all"
                    disabled={internalOnly || !clientAccessEnabled}
                  >
                    <Users className="entity-chat-panel-tab-icon" />
                    {t('tabs.all')}
                    <Badge variant="secondary" className="entity-chat-panel-tab-count">
                      {messageCounts.all}
                    </Badge>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          )}

          {/* Participants — their own bar, because who is on a thread is a property of the thread
              rather than a filter over it, and burying it among the toggles is what made people miss
              that a colleague had been added.

              Scoped to the CURRENT entity, never the multi-entity selection: participants belong to
              one conversation, and a merged list would offer "remove" buttons that each act on a
              different thread. */}
          {user && (
            <div className="entity-chat-panel-participants-bar">
              <EntityChatParticipants
                api={apiService}
                entityTypeId={participantsEntity.entityTypeId}
                entityId={participantsEntity.entityId}
                entityLabel={formatEntityType(participantsEntity.entityTypeId)}
                currentProfileId={user?.profileId}
                canSearchDirectory={user?.type === "internal"}
                canManage={canManageParticipants}
                roleLabels={config.roleLabels}
                t={t}
                onClientAccessChange={setHasClientParticipant}
              />

              {/* Your own notifications for this thread, beside the other people-actions rather than
                  buried among the filters. Two states with a label, because a lone bell icon does not
                  say which way it is about to go — and this is the same mute the participant list
                  applies to anyone else, not a third concept next to the global switch. */}
              {canManageSubscription && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    {/* Wrapper so the tooltip still opens while the button is disabled — resolving, or a
                        subscription write in flight. Browsers dispatch no hover/focus to a disabled
                        control, and TooltipTrigger forwards its handlers onto that control, so it needs a
                        live element to sit on. inline-flex keeps the button from stretching in the row. */}
                    <span style={{ display: "inline-flex" }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={isChangingSubscription || !isSubscriptionResolved}
                        onClick={toggleSubscription}
                        aria-label={subscriptionLabel}
                        className={cn(
                          "entity-chat-panel-subscription-toggle",
                          isMuted && "entity-chat-panel-subscription-toggle-muted"
                        )}
                      >
                        {isMuted ? <BellOff size={14} /> : <Bell size={14} />}
                        <span>{subscriptionLabel}</span>
                      </Button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{subscriptionTooltip}</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          )}

          {/* Sub-header with message count and entity filter */}
          <div
            className="entity-chat-panel-sub-header"
            style={{
              flexShrink: 0,
              padding: '12px 16px',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#f9fafb'
            }}
          >
            {!user ? (
              <div className="entity-chat-panel-loading">{t('messages.loading')}</div>
            ) : (
              <>
                <div className="entity-chat-panel-left-section">
                  {/* Only offered when a watermark exists to filter against — with no read state
                      every message is "unread" and the toggle would do nothing. */}
                  {/* Always present, disabled when there is nothing to filter. A control that appears
                      and disappears makes the toolbar jump and leaves you unsure whether the feature
                      exists at all. */}
                  <div className="entity-chat-panel-unread-filter">
                      <label
                        htmlFor="unread-filter"
                        className="entity-chat-panel-unread-label"
                        title={showUnreadOnly ? t('filters.showAllMessages') : t('filters.showUnreadOnly')}
                      >
                        {t('filters.showUnread')}
                      </label>
                      <Switch
                        checked={showUnreadOnly}
                        onCheckedChange={setShowUnreadOnly}
                        id="unread-filter"
                        disabled={unreadMessageIds.size === 0}
                        className="entity-chat-panel-unread-switch"
                      />
                    </div>

                  <div className="entity-chat-panel-importance-filter">
                    <label
                      htmlFor="importance-filter"
                      className="entity-chat-panel-importance-label"
                      title={showImportantOnly ? t('filters.showAllMessages') : t('filters.showImportantOnly')}
                    >
                      <Filter size={16} className="entity-chat-panel-filter-icon" />
                      {t('filters.showImportant')}
                    </label>
                    <Switch
                      checked={showImportantOnly}
                      onCheckedChange={setShowImportantOnly}
                      id="importance-filter"
                      className="entity-chat-panel-importance-switch"
                    />
                    <span className="entity-chat-panel-importance-icon">!</span>
                  </div>

                  <div className="entity-chat-panel-message-counter" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Home className="entity-chat-panel-counter-icon" />
                    {/* Per TAB, matching the design and the numbers in the tab strip right above it.
                        It used to be the entity total, which never moved when you switched tabs — so
                        it sat next to three counts it agreed with none of. */}
                    <span className="entity-chat-panel-counter-text">
                      {t('messages.counter', {
                        // Client users never leave the Internal tab (the tab strip is internal-only) and
                        // messageCounts.internal is hard-coded 0 for them — so read the client bucket
                        // directly rather than the per-tab value, which would render "Messages / 0".
                        count:
                          user?.type === 'client'
                            ? messageCounts.client
                            : messageTab === 'internal'
                              ? messageCounts.internal
                              : messageTab === 'client'
                                ? messageCounts.client
                                : messageCounts.all
                      })}
                    </span>

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            // Set flag to scroll to bottom after refresh
                            shouldScrollToBottom.current = true
                            await fetchMessages()

                            // Fallback: Always attempt scroll after a frame delay
                            // If lastMessageRef handles it first, the flag will be cleared
                            // If not (demo scenario), this will ensure scrolling happens
                            requestAnimationFrame(() => {
                              if (shouldScrollToBottom.current) {
                                attemptScroll()
                              }
                            })
                          }}
                          className="entity-chat-panel-refresh-button"
                          style={{
                            padding: '4px',
                            height: 'auto',
                            minHeight: '24px'
                          }}
                        >
                          <RefreshCw className="entity-chat-panel-refresh-icon" size={14} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{t('messages.refresh')}</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </div>

                {showRelatedEntities && (
                  <div className="entity-chat-panel-filter-section">
                    <span className="entity-chat-panel-filter-label">{t('filters.relatedEntities')}</span>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="entity-chat-panel-filter-trigger">
                          <span className="entity-chat-panel-filter-trigger-text">{getDropdownDisplayText()}</span>
                          <ChevronDown className="entity-chat-panel-filter-trigger-icon" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        className="entity-chat-panel-filter-popover"
                        align="end"
                        style={{
                          zIndex: 1001
                        }}
                        side="bottom"
                        sideOffset={5}
                        data-theme-instance={instanceClass}
                      >
                        {/* Apply theme styles for popover elements */}
                        {mergedSx && (
                          <style dangerouslySetInnerHTML={{
                            __html: generatePopoverStyles(mergedSx, instanceClass)
                          }} />
                        )}
                        <div className="entity-chat-panel-filter-popover-header">
                          <h4 className="entity-chat-panel-filter-title">
                            {t('filters.selectEntities')}
                          </h4>
                          <p className="entity-chat-panel-filter-description">
                            {t('filters.selectEntitiesDescription')}
                          </p>
                        </div>
                        <div className="entity-chat-panel-filter-options">
                          {/* Select All option */}
                          <div
                            className={cn(
                              "flex items-center space-x-2 p-3 cursor-pointer border-b entity-chat-panel-filter-option entity-chat-panel-filter-select-all",
                              relatedEntities.length <= 1
                                ? "opacity-50 cursor-not-allowed"
                                : "hover:bg-muted/50"
                            )}
                            onClick={relatedEntities.length > 1 ? handleSelectAll : undefined}
                            role="checkbox"
                            aria-checked={isAllSelected}
                            aria-disabled={relatedEntities.length <= 1}
                            tabIndex={relatedEntities.length > 1 ? 0 : -1}
                            aria-label={t('filters.all')}
                            onKeyDown={(e) => {
                              if (relatedEntities.length > 1 && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                handleSelectAll()
                              }
                            }}
                          >
                            <Checkbox
                              checked={isAllSelected}
                              onCheckedChange={relatedEntities.length > 1 ? handleSelectAll : undefined}
                              disabled={relatedEntities.length <= 1}
                              className="entity-chat-panel-filter-checkbox"
                            />
                            <div className="entity-chat-panel-filter-option-content">
                              <span className="entity-chat-panel-filter-option-text">
                                {t('filters.all')}
                              </span>
                            </div>
                          </div>

                          {/* Group by entity type */}
                          {Object.entries(
                            relatedEntities.reduce((groups, entity) => {
                              const type = entity.entityTypeId
                              if (!groups[type]) {
                                groups[type] = {
                                  entities: [],
                                  totalMessages: 0
                                }
                              }
                              groups[type].entities.push(entity)
                              groups[type].totalMessages += entity.messageCount
                              return groups
                            }, {} as Record<string, { entities: RelatedEntity[], totalMessages: number }>)
                          ).map(([entityType, group]: [string, { entities: RelatedEntity[], totalMessages: number }]) => {
                            const entityKeysOfType = group.entities.map(entity => `${entity.entityTypeId}:${entity.entityId}`)
                            const isTypeSelected = entityKeysOfType.some(key => selectedEntities.has(key))
                            const isCurrent = group.entities.some(entity => `${entity.entityTypeId}:${entity.entityId}` === currentEntityKey)

                            return (
                              <div
                                key={entityType}
                                className="entity-chat-panel-filter-option entity-chat-panel-filter-entity-type"
                                onClick={() => handleEntityToggle(entityType)}
                                role="checkbox"
                                aria-checked={isTypeSelected}
                                tabIndex={0}
                                aria-label={formatEntityType(entityType)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleEntityToggle(entityType)
                                  }
                                }}
                              >
                                <Checkbox
                                  checked={isTypeSelected}
                                  onCheckedChange={() => handleEntityToggle(entityType)}
                                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                                  className="entity-chat-panel-filter-checkbox"
                                />
                                <div className="entity-chat-panel-filter-option-content">
                                  <div>
                                    <span className="entity-chat-panel-filter-option-text">
                                      {formatEntityType(entityType)}
                                    </span>
                                    {isCurrent && (
                                      <Badge variant="default" className="entity-chat-panel-filter-current-badge">
                                        {t('filters.current')}
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <Badge variant="secondary" className="entity-chat-panel-filter-count-badge">
                                  {group.totalMessages}
                                </Badge>
                              </div>
                            )
                          })}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Messages Area */}
          <div
            style={{
              flex: 1,
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <div
              ref={messagesContainerRef}
              className="entity-chat-panel-messages"
              style={{
                height: '100%',
                overflowY: 'auto',
                padding: '16px'
              }}
            >
              {!user ? (
                <div className="entity-chat-panel-loading">{t('messages.loading')}</div>
              ) : showLoading ? (
                <div className="entity-chat-panel-loading">{t('messages.loading')}</div>
              ) : selectedEntities.size === 0 ? (
                <div className="entity-chat-panel-empty">
                  {t('messages.noEntitiesSelected')}
                </div>
              ) : filteredMessages.length === 0 ? (
                <div className="entity-chat-panel-empty">
                  {t('messages.empty')}
                </div>
              ) : (
                filteredMessages.map((message, index) => (
                  <MessageThread
                    key={message.id}
                    message={message}
                    selectedCount={selectedCount}
                    user={user}
                    messageTab={messageTab}
                    activeReplyIds={activeReplyIds}
                    replyMessages={replyMessages}
                    setReplyMessage={updateReplyMessage}
                    replyImportance={replyImportance}
                    setReplyImportance={updateReplyImportance}
                    replyHtmlEditorRef={replyHtmlEditorRef}
                    onStartReply={handleStartReply}
                    onCancelReply={handleCancelReply}
                    sendReply={sendReply}
                    getRelativeTime={getRelativeTime}
                    getExactTimestamp={getExactTimestamp}
                    formatEntityBadge={formatEntityBadge}
                    getInitials={getInitials}
                    hasCurrentEntitySelected={hasCurrentEntitySelected}
                    hasAnyEntitySelected={selectedEntities.size > 0}
                    currentEntityKey={currentEntityKey}
                    isUnread={unreadMessageIds.has(message.id)}
                    isFirstUnread={message.id === firstUnreadId}
                    showReadToggle={showReadState}
                    isTogglingRead={isTogglingRead}
                    onToggleRead={toggleMessageRead}
                    t={t}
                    ref={index === filteredMessages.length - 1 ? lastMessageRef : undefined}
                  />
                ))
              )}
            </div>

            {/* Scroll to Bottom Button */}
            {showScrollToBottom && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    onClick={scrollToBottom}
                    className="entity-chat-panel-scroll-to-bottom"
                    size="sm"
                    variant="secondary"
                    style={{
                      position: 'absolute',
                      bottom: '16px',
                      right: '16px',
                      borderRadius: '50%',
                      width: '32px',
                      height: '32px',
                      padding: '0',
                      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)',
                      zIndex: 10
                    }}
                  >
                    <ArrowDown className="entity-chat-panel-scroll-icon" size={18} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('messages.scrollToBottom')}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>

          {/* Message Input Area - moved to bottom */}
          {user && (
            <div
              className="entity-chat-panel-input-area"
              style={{
                flexShrink: 0,
                padding: '16px',
                borderTop: '1px solid #e5e7eb'
              }}
            >
              {/* Warning for current entity not selected */}
              {!hasCurrentEntitySelected && (
                <div className="entity-chat-panel-warning">
                  <strong>{t('input.warningNote')}</strong> {t('input.warningMessage', { entityKey: currentEntityKey })}
                </div>
              )}

              {/* The combined view cannot set an audience, so writing from it is genuinely
                  impossible — but a disabled box explains nothing. Offering the two tabs turns the
                  dead end into a choice, and lands you where the message was going to go anyway. */}
              {user.type === "internal" && messageTab === "all" ? (
                <div className="entity-chat-panel-audience-prompt">
                  <span className="entity-chat-panel-audience-prompt-text">
                    {t('input.pickAudience')}
                  </span>
                  <div className="entity-chat-panel-audience-prompt-actions">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setMessageTab("internal")}
                    >
                      {t('tabs.internal')}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setMessageTab("client")}
                    >
                      {t('tabs.client')}
                    </Button>
                  </div>
                </div>
              ) : (
              <>
              {/* Message input - Tiptap HTML Editor with streamlined interface */}
              <TiptapHtmlEditor
                ref={htmlEditorRef}
                value={newMessage}
                onChange={setNewMessage}
                onSend={sendMessage}
                placeholder={
                  user.type === "client"
                    ? t('input.placeholder')
                    : messageTab === "all"
                      ? t('input.placeholderAllTab')
                      : messageTab === "internal"
                        ? t('input.placeholderInternal')
                        : t('input.placeholderClient')
                }
                disabled={selectedEntities.size === 0 || !hasCurrentEntitySelected || (user.type === "internal" && messageTab === "all")}
                className="entity-chat-panel-html-editor"
                showSendButton={true}
                isImportant={newMessageIsImportant}
                onImportanceChange={setNewMessageIsImportant}
              />

              {/* Who will see this, stated under the box rather than inferred from the tab. The
                  audience is decided by the tab and nothing in the composer says so otherwise. */}
              <p className="entity-chat-panel-audience-hint">
                {messageTab === "internal" ? t('input.visibleInternal') : t('input.visibleClient')}
              </p>
              </>
              )}
            </div>
          )}
        </div>
      </TooltipProvider>
    </div>
  )
}
